import Link from 'next/link';
import { Children, FC, ReactNode } from 'react';
import { IoChevronForward } from 'react-icons/io5';

import { Box, Text, UnstyledButton } from '@mantine/core';

import { CarouselSection } from 'components/section/CarouselSection';
import { useResponsiveNbCols } from 'components/section/useResponsiveNbCols';

import cx from './ProfileRow.module.scss';

const ProfileRow: FC<{
    leading: ReactNode;
    leadingShape?: 'round' | 'square' | 'thumbnail';
    title: ReactNode;
    meta?: ReactNode;
    metaIcon?: ReactNode;
    metaLines?: number | 'all';
    details?: ReactNode;
    detailsIcon?: ReactNode;
    size?: 'md' | 'lg';
    dashed?: boolean;
    href?: string;
    target?: string;
    onClick?: () => void;
}> = ({
    leading,
    leadingShape = 'round',
    title,
    meta,
    metaIcon,
    metaLines,
    details,
    detailsIcon,
    size = 'md',
    dashed,
    href,
    target,
    onClick,
}) => {
    const rowAttributes = { 'data-size': size, 'data-dashed': dashed || undefined };

    const content = (
        <>
            <Box className={cx.leading} data-shape={leadingShape}>
                {leading}
            </Box>
            <Box className={cx.text}>
                <Text className={cx.title}>{title}</Text>
                {meta && (
                    <RowMeta icon={metaIcon} lines={metaLines}>
                        {meta}
                    </RowMeta>
                )}
                {details && <RowMeta icon={detailsIcon}>{details}</RowMeta>}
            </Box>
            {(href || onClick) && <IoChevronForward className={cx.chevron} />}
        </>
    );

    if (href) {
        return (
            <Link href={href} target={target} className={cx.row} {...rowAttributes} data-interactive>
                {content}
            </Link>
        );
    }

    if (onClick) {
        return (
            <UnstyledButton onClick={onClick} className={cx.row} {...rowAttributes} data-interactive>
                {content}
            </UnstyledButton>
        );
    }

    return (
        <Box className={cx.row} {...rowAttributes}>
            {content}
        </Box>
    );
};

const RowMeta: FC<{ icon?: ReactNode; lines?: number | 'all'; children: ReactNode }> = ({ icon, lines, children }) => (
    <Box className={cx.meta} data-wrap={lines ? true : undefined}>
        {icon && (
            <Box component="span" className={cx.metaIcon}>
                {icon}
            </Box>
        )}
        <Text
            component="span"
            className={cx.metaText}
            inherit
            lineClamp={typeof lines === 'number' ? lines : undefined}
        >
            {children}
        </Text>
    </Box>
);

// Match the .grid breakpoints
const ROW_COLS = { xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 3 };
const DENSE_ROW_COLS = { xs: 1, sm: 2, md: 3, lg: 4, xl: 4, xxl: 4 };

const ProfileRowCarousel: FC<{ dense?: boolean; children: ReactNode }> = ({ dense, children }) => {
    const cols = dense ? DENSE_ROW_COLS : ROW_COLS;
    const shownCols = useResponsiveNbCols(0, cols) ?? 1;
    const rows = Children.toArray(children);

    const columns = Array.from({ length: Math.ceil(rows.length / 2) }, (_, index) =>
        rows.slice(index * 2, index * 2 + 2),
    );

    return (
        <Box className={cx.rowList}>
            {/* A single row fits; stacking two high would only scatter so few items */}
            {rows.length <= shownCols ? (
                <Box className={cx.grid} data-dense={dense || undefined}>
                    {rows}
                </Box>
            ) : (
                <CarouselSection cols={cols} slideGap="xs">
                    {columns.map((column, index) => (
                        <Box key={index} className={cx.carouselColumn}>
                            {column}
                        </Box>
                    ))}
                </CarouselSection>
            )}
        </Box>
    );
};

export { ProfileRow, ProfileRowCarousel };
